import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Map,
  useMap,
  MapControl,
  ControlPosition,
} from '@vis.gl/react-google-maps';
import MapLoadingSpinner from '../shared/MapLoadingSpinner';
import CashBidsMarkers from './CashBidsMarkers';
import {
  CFormSelect,
  CButton,
  CFormSwitch,
  CCollapse,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import {cilX} from '@coreui/icons';
import styles from './CashBids.module.css';
import {
  initCashBids,
  fetchCashBidLocations,
  userSelectedCashBidCommodity,
  userSelectedCashBidLocation,
  userSelectedCashBidMonth,
  resetCashBidFilters,
  toggleShowAllLocations,
  hidePurchaseContractInvalidTimeSpanModal,
  hidePurchaseContractNoBidsModal,
} from './cashBids-actions';
import {isMobile} from 'react-device-detect';
import CashBidsModal from './CashBidsModal';
import PurchaseContractModal from './PurchaseContractModal';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

const CashBids = () => {

  const dispatch = useDispatch();

  const map = useMap();

  const {
    sidebarVisible,
    sidebarUnfoldable,
  } = useSelector(state => state.userSettings);

  const {
    locations,
    locationsDropDownListDataSource,
    selectedLocation,
    fetchingLocations,
    commoditiesDropDownListDataSource,
    monthDropDownListDataSource,
    selectedCommodity,
    selectedMonth,
    showAllLocations,
    purchaseContractInvalidTimeSpanModalIsVisible,
    purchaseContractNoBidsModalIsVisible,
  } = useSelector(state => state.cashBids);

  const [zoom, setZoom] = React.useState(parseInt(process.env.REACT_APP_DEFAULT_MAP_ZOOM));

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);

  const [visible, setVisible] = React.useState(false)

  const [searchParams] = useSearchParams();

  React.useEffect(() => {  
    dispatch(initCashBids(searchParams.get('locationId'), searchParams.get('contractType'), searchParams.get('commodity')));
    dispatch(fetchCashBidLocations());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize)
  }, []);

  const handleZoomChange = React.useCallback((event) => {
    setZoom(event?.detail?.zoom);
  }, []);

  const zoomToLocation = (location) => {
    map.panTo({
      lat: location.latitude,
      lng: location.longitude,
    });
    if (map.zoom >= 16) {
      return;
    }
    else {
      var zoomStep = map.zoom;
      const interval = setInterval(() => {
        if (zoomStep === 16) {
          clearInterval(interval);
        }
        map.setZoom(zoomStep++);
      }, 200);
    }
  }

  const height = windowHeight - 64 - (isMobile === true ? 150 : 75);

  var width = windowWidth;

  if (sidebarUnfoldable === true && sidebarVisible === true) {
    width -= 64;
  }
  else if (sidebarUnfoldable === false && sidebarVisible === true) {
    width -= 256;
  }

  return (
    <>
      <div style={{
        height: isMobile === true ? '150px' : '75px',
        width: width,
        justifyContent: 'flex-start', 
        alignContent: 'center',
        display: 'flex',
        paddingTop: '10px',
        paddingLeft: '14px',
        flexDirection: isMobile === true ? 'column' : 'row',
      }}>
        <CFormSelect
          floatingLabel='Zoom To Location'
          className='mb-3'
          style={{width: 300, lineHeight: 4,}}
          value={selectedLocation.locationId}
          options={locationsDropDownListDataSource}
          onChange={e => {
            var index = locations.findIndex(location => location.locationId.toString() === e.target.value);
            dispatch(userSelectedCashBidLocation(locations[index], false));
            zoomToLocation(locations[index]);
          }} />
        {
          isMobile === true ? null : (<div>&nbsp;&nbsp;&nbsp;</div>)
        }
        <div style={{flexDirection: 'column'}}>
          <CButton 
            color='primary'
            variant='outline'
            style={{width: 140, height: 58,}}
            onClick={() => setVisible(!visible)}>Filters</CButton>
          <CCollapse 
            visible={visible} 
            style={{
              backgroundColor: 'white',
              height: 270,
              zIndex: 1,
              padding: 10,
              width: 320,
              position: 'relative',
              borderColor: '#00244B',
              borderWidth: 1,
              borderStyle: 'solid',
              borderRadius: 6,
            }}>
            <CFormSelect
              floatingLabel='Filter By Commodity'
              className='mb-3'
              style={{width: 300, lineHeight: 4,}}
              value={selectedCommodity}
              options={commoditiesDropDownListDataSource}
              onChange={e => dispatch(userSelectedCashBidCommodity(e.target.value))} />
            <CFormSelect
              floatingLabel='Filter By Delivery Month'
              className='mb-3'
              style={{width: 300, lineHeight: 4,}}
              value={selectedMonth}
              options={monthDropDownListDataSource}
              onChange={e => dispatch(userSelectedCashBidMonth(e.target.value))} />
            <CButton
              color='primary'
              variant='outline'
              style={{width: 140, height: 58,}}
              onClick={() => dispatch(resetCashBidFilters())}>
              Clear Filters 
            </CButton>
            <div style={{marginTop: 15}}>
              <CFormSwitch 
                size='lg' 
                label='Show Competitor Bids' 
                value={showAllLocations}
                onChange={() => dispatch(toggleShowAllLocations())} />
            </div>
          </CCollapse>
        </div>
      </div>
      <div style={{
        width: width,
        height: height,
      }}>
        <Map
          mapId='CASH_BID_LOCATIONS'
          defaultCenter={{
            lng: -88.257311,
            lat: 40.112519,
          }} 
          defaultZoom={zoom}
          fullscreenControl={false}
          mapTypeControl={true}
          streetViewControl={false}
          onZoomChanged={handleZoomChange}>
          {
            fetchingLocations === true ? (
              <MapControl position={ControlPosition.CENTER}>
                <MapLoadingSpinner loadingText='Fetching Locations & Cash Bids' />
              </MapControl>
            ) : null
          }
          <CashBidsMarkers locations={locations} zoom={zoom} />
        </Map>
      </div>
      <CashBidsModal />
      <PurchaseContractModal />
      <CModal
        alignment='center'
        visible={purchaseContractInvalidTimeSpanModalIsVisible}>
        <CModalHeader closeButton={false}>
          <CModalTitle className={styles.cashBidsModalTitle}>
            Purchase Contract Error
          </CModalTitle>
          <CButton className={styles.modalCloseButton} onClick={() => dispatch(hidePurchaseContractInvalidTimeSpanModal())}>
            <CIcon className={styles.modalCloseButtonIcon} icon={cilX} />
          </CButton>
        </CModalHeader>
        <CModalBody>
          Live grain contracting is currently only available between the hours of 7AM CST & 4PM CST. Future functionality 24/7 is planned.
        </CModalBody>
      </CModal>
      <CModal
        alignment='center'
        visible={purchaseContractNoBidsModalIsVisible}>
        <CModalHeader closeButton={false}>
          <CModalTitle className={styles.cashBidsModalTitle}>
            Purchase Contract Error
          </CModalTitle>
          <CButton className={styles.modalCloseButton} onClick={() => dispatch(hidePurchaseContractNoBidsModal())}>
            <CIcon className={styles.modalCloseButtonIcon} icon={cilX} />
          </CButton>
        </CModalHeader>
        <CModalBody>
          No cash bids are currently available for the selected commodity at this location.
        </CModalBody>
      </CModal>
    </>
  );
}

export default CashBids;
